import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Radio, PhoneCall, ShieldOff, LogOut, Orbit, CalendarClock, BarChart3, Megaphone, Star, Ban, Calendar, Settings as SettingsIcon, Building2, Gift, Mic, ExternalLink, ChevronDown, Wrench, Pin, PinOff, LifeBuoy } from "lucide-react";
import { api } from "../lib/api";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/campaigns", label: "Campaigns", icon: Radio, testid: "nav-campaigns" },
  { to: "/calls", label: "Call Log", icon: PhoneCall, testid: "nav-calls" },
  { to: "/appointments", label: "Appointments", icon: Calendar, testid: "nav-appointments" },
  { to: "/follow-ups", label: "Follow-ups", icon: CalendarClock, testid: "nav-follow-ups" },
  { to: "/clients", label: "Clients", icon: Building2, testid: "nav-clients" },
  { to: "/tickets", label: "Tickets", icon: LifeBuoy, testid: "nav-tickets" },
  { to: "/agent-personas", label: "AI Voices", icon: Mic, testid: "nav-agent-personas" },
  { to: "/referrals", label: "Referrals", icon: Gift, testid: "nav-referrals" },
  { to: "/consent", label: "Hear Ashley", icon: Megaphone, testid: "nav-consent" },
  { to: "/feedback", label: "Feedback", icon: Star, testid: "nav-feedback" },
  { to: "/performance", label: "Performance", icon: BarChart3, testid: "nav-performance" },
  { to: "/dnc", label: "DNC List", icon: ShieldOff, testid: "nav-dnc" },
  { to: "/blocked", label: "Blocked", icon: Ban, testid: "nav-blocked" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const PINNED_KEY = "ra_tools_pinned_v1";
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const ToolLink = ({ link, pinned, onTogglePin }) => (
  <div className="group/link flex items-center gap-1 pr-1 rounded-md hover:bg-[#FFB000]/5 transition-colors">
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`tool-link-${slug(link.name)}`}
      className="flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12.5px] text-white/50 hover:text-[#FFB000] transition-colors min-w-0"
    >
      <span className="truncate">{link.name}</span>
      <ExternalLink className="w-3 h-3 ml-auto opacity-0 group-hover/link:opacity-50 flex-shrink-0" strokeWidth={1.75} />
    </a>
    <button
      type="button"
      onClick={() => onTogglePin(link.url)}
      title={pinned ? "Unpin from top" : "Pin to top"}
      data-testid={`tool-pin-${slug(link.name)}`}
      className={`flex-shrink-0 p-1 rounded transition-all ${
        pinned
          ? "text-[#FFB000] opacity-100"
          : "text-white/30 opacity-0 group-hover/link:opacity-100 hover:text-[#FFB000]"
      }`}
    >
      {pinned ? <PinOff className="w-3 h-3" strokeWidth={2} /> : <Pin className="w-3 h-3" strokeWidth={2} />}
    </button>
  </div>
);

export const Sidebar = () => {
  const nav = useNavigate();
  const user = JSON.parse(localStorage.getItem("ra_user") || "null");

  // Tool list is admin-only — fetched on mount so the URLs never ship in the public JS bundle.
  const [toolGroups, setToolGroups] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/tools").then(({ data }) => {
      if (!cancelled) setToolGroups(data?.groups || []);
    }).catch(() => {
      // silent — clients/non-admins simply don't see the tools section
    });
    return () => { cancelled = true; };
  }, []);

  // Flat URL → link lookup, rebuilt whenever the tool list arrives.
  const toolByUrl = React.useMemo(() => {
    const map = {};
    toolGroups.forEach((g) => g.links.forEach((l) => { map[l.url] = l; }));
    return map;
  }, [toolGroups]);

  const [pinnedUrls, setPinnedUrls] = useState(() => {
    try {
      const raw = localStorage.getItem(PINNED_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(PINNED_KEY, JSON.stringify(pinnedUrls));
    } catch {
      // localStorage unavailable — pin state simply won't persist across reloads
    }
  }, [pinnedUrls]);

  const togglePin = useCallback((url) => {
    setPinnedUrls((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
    );
  }, []);

  const isPinned = (url) => pinnedUrls.includes(url);

  const logout = () => {
    localStorage.removeItem("ra_token");
    localStorage.removeItem("ra_user");
    nav("/login");
  };

  return (
    <aside
      data-testid="sidebar"
      className="fixed left-0 top-0 h-screen w-64 bg-[#0a0a0b] border-r border-white/5 flex flex-col z-30 hidden md:flex"
    >
      <div className="px-6 py-6 flex items-center gap-3 border-b border-white/5">
        <div className="relative">
          <Orbit className="w-7 h-7 text-[#FFB000]" strokeWidth={1.5} />
          <div className="absolute inset-0 blur-xl bg-[#FFB000]/20 rounded-full" />
        </div>
        <div>
          <div className="text-white font-medium text-[15px] tracking-tight leading-none">
            Repliant Arc
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-white/40 mt-1">
            Command Center
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-6 space-y-1">
        {NAV.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-[#FFB000]/10 text-[#FFB000] border border-[#FFB000]/20"
                  : "text-white/60 hover:text-white hover:bg-white/[0.04] border border-transparent"
              }`
            }
          >
            <Icon className="w-4 h-4" strokeWidth={1.75} />
            <span className="font-medium">{label}</span>
          </NavLink>
        ))}

        {/* ── Tools (admin-only external links, fetched from backend) ── */}
        {toolGroups.length > 0 && (
          <div className="pt-5 mt-3 border-t border-white/5" data-testid="sidebar-tools">
            <div className="flex items-center gap-2 px-3 mb-2 text-[10px] font-mono uppercase tracking-[0.15em] text-white/30">
              <Wrench className="w-3 h-3" strokeWidth={1.75} />
              <span>Tools</span>
            </div>

            {/* Pinned (shows above categories when any tool is pinned + still in the live list) */}
            {pinnedUrls.length > 0 && pinnedUrls.some((u) => toolByUrl[u]) && (
              <div className="mb-3" data-testid="tools-pinned">
                <div className="flex items-center gap-1.5 px-3 mb-1 text-[10px] font-mono uppercase tracking-[0.12em] text-[#FFB000]/70">
                  <Pin className="w-2.5 h-2.5" strokeWidth={2.25} />
                  <span>Pinned</span>
                </div>
                <div className="ml-2 pl-3 border-l border-[#FFB000]/15 space-y-0.5">
                  {pinnedUrls
                    .map((url) => toolByUrl[url])
                    .filter(Boolean)
                    .map((link) => (
                      <ToolLink key={link.url} link={link} pinned onTogglePin={togglePin} />
                    ))}
                </div>
              </div>
            )}

            {toolGroups.map((group) => (
              <details
                key={group.label}
                className="group [&_summary::-webkit-details-marker]:hidden"
                data-testid={group.testid}
              >
                <summary className="cursor-pointer list-none flex items-center justify-between px-3 py-1.5 rounded-md text-[13px] text-white/55 hover:text-white hover:bg-white/[0.03] transition-colors">
                  <span className="font-medium">{group.label}</span>
                  <ChevronDown className="w-3.5 h-3.5 transition-transform group-open:rotate-180" strokeWidth={2} />
                </summary>
                <div className="mt-1 mb-2 ml-2 pl-3 border-l border-white/5 space-y-0.5">
                  {group.links.map((link) => (
                    <ToolLink
                      key={link.url}
                      link={link}
                      pinned={isPinned(link.url)}
                      onTogglePin={togglePin}
                    />
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}
      </nav>

      <div className="border-t border-white/5 px-4 py-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FFB000]/30 to-[#FFB000]/5 border border-[#FFB000]/20 flex items-center justify-center text-[#FFB000] text-sm font-semibold">
            {(user?.name || "A").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm text-white/90 truncate" data-testid="sidebar-user-name">
              {user?.name || "Admin"}
            </div>
            <div className="text-[11px] text-white/40 truncate font-mono">{user?.email}</div>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-white/60 hover:text-white hover:bg-white/[0.04] border border-white/10 transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" />
          Sign out
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
